import { baseFormat, splitFormatAmount } from '@/utility/formatter';
import { useState } from 'react';
import { Cell, Label, Pie, PieChart, Tooltip } from 'recharts';

const TOOLTIP_ENABLED = false;

const InvestmentOverview = ({ activeProducts, data, bankAccountDetails }) => {
  const [hoveredCell, setHoveredCell] = useState(-1);

  const hasMutualFund = activeProducts.find((product) => product.id === 'mutualFunds');
  const hasBankAccount = activeProducts.find((product) => product.id === 'bank');

  const overviewData = [
    { name: 'Stocks', color: '#75FFFF', value: data?.stocks?.current || 0 },
    hasMutualFund && {
      name: 'Mutual Fund',
      color: '#003E48',
      value: data?.mutualFunds?.current || 0,
    },
    hasBankAccount && {
      name: 'Bank',
      color: '#FFD747',
      value: bankAccountDetails?.availableBalance || 0,
    },
  ].filter((p) => p);

  const onPieEnter = (e, index) => {
    setHoveredCell(index);
  };

  const onPieLeave = (e) => {
    setHoveredCell(-1);
  };

  const calculatedTotalAmount = overviewData.reduce((acc, data) => {
    if (data.value) return acc + data.value;
    else return acc;
  }, 0);

  const formattedTotalAmount = splitFormatAmount(calculatedTotalAmount);

  return (
    <div className="tw-border tw-relative tw-border-t-[4px] tw-border-t-[#003E48] tw-min-h-40 tw-rounded-[16px] tw-overflow-hidden tw-p-4">
      <div className="tw-absolute tw-top-[-35px] tw-left-[50%] tw-translate-x-[-50%] tw-bg-[#003E48] tw-w-[140px] tw-text-[14px] tw-pt-10 tw-pb-1 tw-text-[#00FFCC] tw-rounded-bl-[30px] tw-rounded-br-[30px] tw-flex tw-justify-center">
        My Net-worth
      </div>

      <div
        className={`tw-flex tw-pt-5 tw-h-full tw-gap-4 max-sm:tw-gap-0 max-sm:tw-flex-col max-sm:tw-mt-4`}
      >
        <div className="tw-flex tw-justify-center tw-gap-6 sm:tw-gap-[50px] tw-items-center max-sm:tw-scale-[0.9] max-sm:tw-transform max-sm:tw-origin-center">
          <PieChart width={200} height={200}>
            <Pie
              data={overviewData}
              cx={90}
              cy={90}
              innerRadius={40}
              outerRadius={90}
              fill="#8884d8"
              paddingAngle={0}
              cornerRadius={0}
              dataKey="value"
              activeShape={null}
              isAnimationActive={true}
            >
              <Label
                value={
                  hoveredCell != -1
                    ? Number.parseFloat(
                        (overviewData[hoveredCell]?.value * 100) / calculatedTotalAmount
                      ).toFixed(0) + '%'
                    : ''
                }
                position="center"
              />
              {overviewData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={overviewData[index % overviewData.length].color}
                  onMouseEnter={(e) => onPieEnter(e, index)}
                  onMouseLeave={onPieLeave}
                  style={{ cursor: 'pointer' }}
                />
              ))}
            </Pie>
            {TOOLTIP_ENABLED && <Tooltip content={<CustomTooltip payload={overviewData} />} />}
          </PieChart>
        </div>
        <div className="tw-flex tw-flex-col tw-w-full tw-pr-3 max-sm:tw-px-0 tw-justify-around">
          <div className="tw-flex tw-flex-col max-sm:tw-flex-row  max-sm:tw-justify-between max-sm:tw-items-center">
            <div className="tw-text tw-text-[#001316] tw-opacity-70">Total Net-worth</div>
            <div className="tw-text-lg sm:tw-text-xl md:tw-text-3xl">
              <span className="xs:tw-text-3xl md:tw-text-5xl">
                {formattedTotalAmount.symbol}
                {formattedTotalAmount.integer}.
              </span>
              <span className="">{formattedTotalAmount.decimal || '00'}</span>
              {formattedTotalAmount.notation}
            </div>
          </div>
          <div className="chart-legend max-sm:tw-mt-2">
            {overviewData.map((data, index) => {
              if (data.value)
                return (
                  <div
                    key={`legend-${data.name}`}
                    className={`tw-px-3 tw-py-1 tw-flex tw-items-center tw-justify-between tw-rounded-md ${index == hoveredCell ? 'tw-bg-[#BFE2E2]' : 'tw-bg-transparent'}`}
                  >
                    <div className="tw-flex tw-items-center tw-gap-3">
                      <div className={`tw-bg-[${data.color}] tw-h-3 tw-w-3 tw-rounded-full`}></div>
                      <div className="tw-text">{data.name}</div>
                    </div>
                    <div className="tw-text">{baseFormat(data.value)}</div>
                  </div>
                );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="tw-bg-white tw-p-[5px] tw-text-[14px] tw-rounded-md tw-shadow-md">
        {payload[0].name}
      </div>
    );
  }
  return null;
};

export default InvestmentOverview;
